import { useEffect, useRef, useState } from "react";
import { Alert, Autocomplete, Button, Stack, Text, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { activateVersion, getEnvironments, getFlows } from "../api";
import { notifications } from "@mantine/notifications";

function namesOnFlow(flow) {
  return Object.keys(flow?.activeVersions ?? {}).filter((key) => key.trim());
}

export default function FlowEnvironments({ flow, version, close, reload }) {
  // Pre-fill: use first existing environment, or default to "production"
  const defaultEnv = namesOnFlow(flow)[0] || "production";
  const [value, setValue] = useState(defaultEnv);
  const [comment, setComment] = useState("");
  const [suggestions, setSuggestions] = useState(() =>
    namesOnFlow(flow).filter((key) => flow.activeVersions[key] !== version?.id)
  );
  const [submitting, setSubmitting] = useState(false);
  const knownNamesRequest = useRef(null);
  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      environment: "",
    },
  });

  function loadKnownNames() {
    if (!knownNamesRequest.current) {
      knownNamesRequest.current = Promise.all([getEnvironments(), getFlows()])
        .then(([envs, flows]) => {
          const names = new Set(namesOnFlow(flow));
          for (const env of envs ?? []) {
            if (env?.name) names.add(env.name);
          }
          for (const item of flows ?? []) {
            for (const key of namesOnFlow(item)) names.add(key);
          }
          return names;
        })
        .catch(() => new Set(namesOnFlow(flow)));
    }
    return knownNamesRequest.current;
  }

  useEffect(() => {
    let cancelled = false;
    loadKnownNames().then((names) => {
      if (cancelled) return;
      const list = [...names].filter(
        (name) => flow.activeVersions?.[name] !== version?.id
      );
      list.sort((a, b) => a.localeCompare(b));
      setSuggestions(list);
    });
    return () => {
      cancelled = true;
    };
    // loadKnownNames closes over the flow from this mount. The modal remounts per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version?.id]);

  if (!version) return null;

  const doActivate = (name) => {
    setSubmitting(true);
    activateVersion(flow.id, version.id, name, comment || null)
      .then((result) => {
        form.setFieldValue("environment", "");
        setComment("");
        notifications.show({
          title: "Activated",
          message: `Version now active for environment '${name}'`,
        });
        // Promotion-time validation warnings (warn-and-allow): the activation stands,
        // but e.g. a routing rule not enabled for this environment would make requests
        // fail at runtime until the config is fixed.
        if (result?.warnings?.length > 0) {
          notifications.show({
            title: "Activation warnings",
            message: result.warnings.join("\n"),
            color: "yellow",
            autoClose: false,
          });
        }
        close();
        reload();
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div>
      <Alert mb="md">
        Makes this version live for API calls in the selected environment.
      </Alert>
      <form
        onSubmit={form.onSubmit(() => {
          const name = value.trim();
          if (!name) {
            notifications.show({
              title: "Environment required",
              message: "Enter an environment name.",
              color: "red",
            });
            return;
          }

          // Re-pinning a name this flow already uses is not a new environment.
          if (Object.prototype.hasOwnProperty.call(flow.activeVersions ?? {}, name)) {
            doActivate(name);
            return;
          }

          setSubmitting(true);
          loadKnownNames()
            .then((known) => {
              if (known.has(name)) {
                doActivate(name);
                return;
              }
              setSubmitting(false);
              modals.openConfirmModal({
                title: `Really create new environment '${name}'?`,
                children: (
                  <Text size="sm">
                    '{name}' is not used by any flow yet. Activating adds it to
                    this flow, and the name shows up wherever environments are
                    listed.
                  </Text>
                ),
                labels: { confirm: "Create and activate", cancel: "Cancel" },
                onConfirm: () => doActivate(name),
              });
            })
            .catch(() => setSubmitting(false));
        })}
      >
        <Stack>
          <Autocomplete
            label="Environment"
            description="The environment name used in API calls to select which version to run"
            placeholder="e.g. production, staging"
            data={suggestions}
            value={value}
            onChange={setValue}
          />

          <Textarea
            label="Comment (optional)"
            placeholder="Describe what changed in this version..."
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            minRows={2}
          />

          <Button type="submit" loading={submitting}>
            Activate
          </Button>
        </Stack>
      </form>
    </div>
  );
}
